import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import {
  AppointmentModel,
  AUTHORIZATION_PROCEDURE_STATUS,
  AUTHORIZATION_STATUS,
  ClinicService,
  ConfirmService,
  CustomFieldService,
  dateRangeValidator,
  Facility,
  getAuthorizationStatusesWithout,
  LimitationQuantityType,
  Limitations,
  OutdatedFlags,
  PatientInsurance,
} from '@mmx/shared'
import moment from 'moment-timezone'
import { combineLatestWith, map, startWith, Subject, takeUntil, tap } from 'rxjs'

import { AuthCptPickerValue } from '../auth-cpt-picker'

@Component({
  selector: 'mmx-auth-details-form',
  templateUrl: './auth-details-form.component.html',
  styleUrls: ['./auth-details-form.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AuthDetailsFormComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AuthDetailsFormComponent,
      multi: true,
    },
  ],
})
export class AuthDetailsFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input()
  public includeUnits = false

  @Input()
  public diagnosisCodes: string[]

  @Input()
  public timezone: string

  @Input()
  public outdated = false

  @Input()
  public ignoredOutdatedConflicts = false

  @Input()
  public outdatedWarnings: OutdatedFlags[] = []

  @Input()
  public patientInsurances: PatientInsurance[]

  @Input()
  public appointment: AppointmentModel

  @Input()
  public suggestedProcedureCodes: string[] = []

  @Input()
  public manuallyEditedFields: Set<string> = new Set()

  @Input()
  public lockingField: string | null = null

  @Output()
  public unlockField = new EventEmitter<string>()

  @Output()
  public lockField = new EventEmitter<string>()

  @Output()
  public ignoreOutdatedConflicts = new EventEmitter<void>()

  @Output()
  public pullStatusClicked = new EventEmitter<void>()

  public readonly OutdatedFlags = OutdatedFlags
  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS

  public systemNotesEditing = false

  public get separateUserAndSystemAuthNotesEnabled(): boolean {
    return this.clinicService.clinic?.modulesPreferences?.separateUserAndSystemAuthNotes ?? false
  }

  public get preventAuthManualOverride(): boolean {
    return !!(this.clinicService.clinic?.modulesPreferences?.preventAuthManualOverride)
  }

  public isFieldUnlocked(field: string): boolean {
    return this.preventAuthManualOverride && !(this.manuallyEditedFields?.has(field))
  }

  public toggleSystemNotesEdit(): void {
    if (!this.separateUserAndSystemAuthNotesEnabled) {
      return
    }

    this.systemNotesEditing = !this.systemNotesEditing
    const ctrl = this.formGroup.get('note')
    if (this.systemNotesEditing) {
      ctrl?.enable()
    } else {
      ctrl?.disable()
    }
  }

  public readonly authorizationStatuses = getAuthorizationStatusesWithout([
    AUTHORIZATION_STATUS.NONE,
  ])

  public get procedureCodesDisplay() {
    return (this.appointment?.products || [])
      .flatMap(p => p.getCPTCodes())
      .filter((v, i, a) => v && a.indexOf(v) === i)
      .join(', ')
  }

  public get cptPickerEmptyMessage(): string {
    const appointmentCpts = (this.appointment?.products || [])
      .flatMap(p => p.getCPTCodes())
      .filter(Boolean)

    const hasProcedureCodes = !!Object.keys(this.formGroup.get('procedureCodes')?.value || {}).length
    const allAppointmentCptsCovered = this.suggestedProcedureCodes?.length === 0

    if (appointmentCpts.length > 0 && !hasProcedureCodes && allAppointmentCptsCovered) {
      return 'dialog.authorization.procedures.all-covered'
    }

    return 'dialog.authorization.procedures.empty'
  }

  private readonly numberValidators = [
    Validators.minLength(6),
    Validators.maxLength(50),
  ]

  public formGroup = this.fb.group({
    id: this.fb.control<string>(null),
    number: this.fb.control<string>('', this.numberValidators),
    referenceNumber: this.fb.control<string>('', this.numberValidators),
    facility: this.fb.control<Facility>(null, [Validators.required]),
    facilityId: this.fb.control<string>(null),
    facilityNPI: this.fb.control<string>(null),
    procedureCodes: this.fb.control<AuthCptPickerValue>(null, Validators.required),
    diagnosisCodes: this.fb.control<string[]>(null),
    insuranceId: this.fb.control<string>(null, [Validators.required]),
    carrierId: this.fb.control<string>(null),
    carrierLookupId: this.fb.control<string>(null),
    referrerId: this.fb.control<string>(null),
    providerIds: this.fb.control<string[]>(null),
    startAt: this.fb.control<moment.Moment>(null),
    endAt: this.fb.control<moment.Moment>(null),
    status: this.fb.control<AUTHORIZATION_STATUS>(null, [Validators.required]),
    limitations: this.fb.group({
      quantity: this.fb.control<number>(null, [Validators.min(0)]),
      quantityType: this.fb.control<LimitationQuantityType>(null),
    }, { validators: this.unitsValidator() }),
    customFields: this.fb.control({}),
    note: this.fb.control<string>({ value: '', disabled: true }, [Validators.maxLength(500)]),
    manualNotes: this.fb.control<string>('', [Validators.maxLength(500)]),
  }, { validators: dateRangeValidator('startAt', 'endAt') } )

  public diff$ = this.formGroup.get('startAt').valueChanges.pipe(
    startWith(this.formGroup.value.startAt),
    combineLatestWith(this.formGroup.get('endAt').valueChanges.pipe(
      startWith(this.formGroup.value.endAt),
    )),
    map(([startAt, endAt]) => {
      if (moment.isMoment(startAt) && moment.isMoment(endAt)) {
        return endAt.diff(startAt, 'days')
      }
    }),
  )

  private onDestroy$ = new Subject<void>()
  private onChange: (value: any) => void = () => {}
  private onTouched: () => void = () => {}
  private onValidatorChange: () => void = () => {}

  constructor(
    private readonly clinicService: ClinicService,
    public readonly customFieldsService: CustomFieldService,
    private readonly fb: FormBuilder,
    private readonly confirmService: ConfirmService,
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnInit() {
    const noteCtrl = this.formGroup.get('note')
    if (this.separateUserAndSystemAuthNotesEnabled) {
      noteCtrl?.disable({ emitEvent: false })
    } else {
      noteCtrl?.enable({ emitEvent: false })
    }

    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        map(() => this.formatValue()),
      )
      .subscribe((value) => {
        this.onChange?.(value)
      })

    this.formGroup.statusChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.onValidatorChange?.()
      })

    this.formGroup.get('status').valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        startWith(this.formGroup.value.status),
      )
      .subscribe((status) => {
        this.handleNumberValidation(status)
        this.handleDateTimeValidation(status)
        this.handleReferenceNumberValidation(status)
        this.handleFacilityValidation(status)
      })

    // ensure we update the carrierId when the selected patient insurance changes
    this.formGroup.get('insuranceId').valueChanges.pipe(
      takeUntil(this.onDestroy$),
      map((insuranceId) => this.patientInsurances.find(ins => ins.id === insuranceId)),
      tap((insurance) => {
        this.formGroup.patchValue({
          carrierLookupId: insurance?.carrierLookupId ?? null,
          carrierId: insurance?.carrierId ?? null,
        })
        this.formGroup.updateValueAndValidity()
      }),
    ).subscribe()
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onStatusSelected(status: AUTHORIZATION_STATUS): void {
    if (status !== AUTHORIZATION_STATUS.NO_ACTION_REQUIRED) {
      return
    }

    const procedureCodes = this.formGroup.get('procedureCodes')

    if (!Object.keys(procedureCodes.value || {}).length) {
      return
    }

    this.confirmService
      .confirm({
        message: 'dialog.authorization.procedures.apply-no-action-required',
        icon: 'help_outline',
        iconColor: 'primary',
        width: '440px',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return
        }

        const codes = procedureCodes.value || {}
        procedureCodes.setValue(
          Object.fromEntries(
            Object.entries(codes).map(([code, data]) => [
              code,
              { ...data, statusCode: AUTHORIZATION_PROCEDURE_STATUS.NOT_REQUIRED },
            ]),
          ),
        )
      })
  }

  public writeValue(value: any): void {
    this.formGroup.patchValue(value || {})
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn
  }

  public validate(): ValidationErrors | null {
    return this.formGroup.valid ? null : { invalid: true }
  }

  public setTo(event: Event, controlName: string, offset: number, units: 'days' | 'months' | 'years' = 'days') {
    event.preventDefault()
    const control = this.formGroup.get(controlName)
    // Take the local date component from startAt (when setting endAt) or from the current time, then convert that date to UTC
    const baseDate = controlName === 'endAt' && this.formGroup.value.startAt
      ? moment(this.formGroup.value.startAt)
      : moment()
    control.setValue(moment.utc(baseDate.format('YYYY-MM-DD')).add(offset, units).startOf('day'))
    control.updateValueAndValidity()
  }

  /**
   * This method checks if a specific outdated flag is still applicable based on the current form values.
   * If the corresponding form control is not touched, it assumes the flag is still valid.
   */
  public isFieldLocked(field: string): boolean {
    if (!this.clinicService.clinic?.modulesPreferences?.preventAuthManualOverride) {
      return false
    }
    return this.manuallyEditedFields?.has(field) ?? false
  }

  public hasOutdatedFlag(flag: OutdatedFlags): boolean {
    const hasFlag = this.outdatedWarnings?.includes(flag)

    switch (flag) {
      case OutdatedFlags.WrongFacility:
        if (this.formGroup.get('facility').touched || this.formGroup.get('facility').value) {
          const appointmentLocationNpi = this.appointment?.location?.provider?.id
          const authLocationNpi = this.formGroup.value.facility?.provider?.id

          return appointmentLocationNpi !== authLocationNpi
        }
        break

      case OutdatedFlags.ConflictDates:
        if (
          this.formGroup.get('startAt').value ||
          this.formGroup.get('endAt').value ||
          this.formGroup.get('startAt').touched ||
          this.formGroup.get('endAt').touched
        ) {
          if (!(this.formGroup.get('startAt').value || this.formGroup.get('endAt').value)) {
            return false
          }

          const appointmentStart = this.appointment?.startAt ? moment.tz(this.appointment.startAt, this.timezone).startOf('day') : null
          const { startAt, endAt } = this.formGroup.value

          if (appointmentStart && startAt && endAt) {
            return !appointmentStart.isBetween(
              moment.tz(startAt.format('YYYY-MM-DD'), this.timezone),
              moment.tz(endAt.format('YYYY-MM-DD'), this.timezone),
              'days',
              '[]',
            )
          }
        }
        break

      case OutdatedFlags.MissingProcedures:
        if (this.formGroup.get('procedureCodes').touched) {
          const procedureCodesOnAuth = Object.keys(this.formGroup.get('procedureCodes').value || {})
          const procedureCodesOnAppointment = (this.appointment?.products || []).flatMap(p => p.getCPTCodes()).filter((v, i, a) => v && a.indexOf(v) === i)

          if (procedureCodesOnAppointment.length > 0) {
            return !procedureCodesOnAppointment.every((code) => procedureCodesOnAuth.includes(code))
          }
        }
        break
    }

    return hasFlag
  }

  private formatValue() {
    const value = this.formGroup.value

    const formattedValue = {
      status: value.status,
      number: value.number,
      referenceNumber: value.referenceNumber,
      insuranceId: value.insuranceId,
      carrierLookupId: value.carrierLookupId,
      carrierId: value.carrierId,
      facility: value.facility,
      facilityId: value.facility?.id,
      npi: value.facility?.provider?.id || this.clinicService.clinic?.provider?.id,
      referrerId: value.referrerId,
      providerIds: value.providerIds,
      procedureCodes: value.procedureCodes,
      diagnosisCodes: value.diagnosisCodes,
      startAt: value.startAt,
      endAt: value.endAt,
      customFields: value.customFields,
      note: value.note,
      priorAuthorization: undefined,
      limitations: this.includeUnits && value.limitations?.quantity != null ? {
        quantity: value.limitations.quantity,
        quantityType: value.limitations.quantityType,
      } : undefined,
    }

    if (this.separateUserAndSystemAuthNotesEnabled) {
      return {
        ...formattedValue,
        manualNotes: value.manualNotes,
      }
    }

    return formattedValue
  }

  private handleNumberValidation(status: AUTHORIZATION_STATUS): void {
    const number = this.formGroup.get('number')
    if ([AUTHORIZATION_STATUS.APPROVED, AUTHORIZATION_STATUS.PARTIALLY_APPROVED].includes(status)) {
      number.setValidators([Validators.required, ...this.numberValidators])
    } else {
      number.setValidators(this.numberValidators)
    }
    number.updateValueAndValidity()
  }

  private handleDateTimeValidation(status: AUTHORIZATION_STATUS): void {
    const startAt = this.formGroup.get('startAt')
    const endAt = this.formGroup.get('endAt')
    if ([AUTHORIZATION_STATUS.APPROVED, AUTHORIZATION_STATUS.PARTIALLY_APPROVED].includes(status)) {
      startAt.setValidators([Validators.required])
      endAt.setValidators([Validators.required])
    } else {
      startAt.clearValidators()
      endAt.clearValidators()
    }
    startAt.updateValueAndValidity()
    endAt.updateValueAndValidity()
  }

  private handleReferenceNumberValidation(status: AUTHORIZATION_STATUS): void {
    const refNumber = this.formGroup.get('referenceNumber')
    if ([AUTHORIZATION_STATUS.INITIATED_CASE_INSURANCE, AUTHORIZATION_STATUS.INITIATED_CASE_MEDICAL].includes(status)) {
      refNumber.setValidators([Validators.required, ...this.numberValidators])
    } else {
      refNumber.setValidators(this.numberValidators)
    }
    refNumber.updateValueAndValidity()
  }

  // eslint-disable-next-line
  private handleFacilityValidation(status: AUTHORIZATION_STATUS): void {
    // const facilityControl = this.formGroup.get('facility')
    // if ([AUTHORIZATION_STATUS.NOT_REQUIRED].includes(status)) {
    //   facilityControl.setValidators([Validators.required])
    // } else {
    //   facilityControl.setValidators([])
    // }
    // facilityControl.updateValueAndValidity()
  }

  private unitsValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const limitations: Limitations = formGroup.value
      if (!limitations.quantity != !limitations.quantityType) {
        if (!limitations.quantity) {
          formGroup.controls.quantity.setErrors({ required: true })
        } else {
          formGroup.controls.quantityType.setErrors({ required: true })
        }
      }

      return null
    }
  }
}
